package gr.tech.pdfgenerator.util

import kotlin.math.*

/* *
*
* PARSING INPUT WORK
*
* */

// Function to parse input string and convert to cents
fun parseCurrencyInput(input: String): Long? {
    // Replace any non-numeric characters except for the decimal point
    val sanitizedInput = input.replace("[^0-9.]".toRegex(), "")
    return try {
        // Split the input by the decimal point
        val parts = sanitizedInput.split(".")
        val dollars = parts[0].toLong() * 100
        val cents = if (parts.size > 1) {
            when (parts[1].length) {
                0 -> 0L
                1 -> parts[1].toLong() * 10
                2 -> parts[1].toLong()
                else -> parts[1].substring(0, 2).toLong() // More than 2 decimal places, truncate
            }
        } else {
            0L
        }
        dollars + cents
    } catch (e: NumberFormatException) {
        null // Return null if the input is not a valid number
    }
}

/* *
*
* FORMATTING WORK
*
* */

fun Double.roundToOneDecimalPlace(): Double {
    return round(this * 10) / 10
}
fun Double.roundToTwoDecimalPlace(): Double {
    return round(this * 100) / 100
}
fun Double.format(digits: Int): String = this.asDynamic().toFixed(digits) as String
fun Float.format(digits: Int): String = this.asDynamic().toFixed(digits) as String
fun Long.roundCents(): Long {
    //digits / 100.0 = Turning Long cents into Double dollars.cents
    //(dollars.cents).roundToLong() = rounding the cents down/up to nearest dollar.
    //(dollars rounded) * 100 = Turning back into cents.
    return ((this / 100.0).roundToLong() * 100)
}
fun Long.toDollars(): Long {
    return this.roundCents() / 100
}

//Used for PDF Graph, values needed to be displayed in $K.
fun Long.toThousandDollars(): String {
    return "$${this/ 1000}k"
}

//Function to convert cents (Long) back to a formatted string.
//-Divides the cents amount by 100 to get it in dollars.
fun formatCurrency(cents: Long): String {
    val dollars = cents / 100
    val remainingCents = (cents % 100).absoluteValue
    if (dollars >= 0) {
        val finalNumb = formatNumber("$dollars.${remainingCents.toString().padStart(2, '0')}")
        return "$${finalNumb}"
    } else {
        val finalNumb = formatNumber("${dollars.absoluteValue}.${remainingCents.toString().padStart(2, '0')}")
        return "-$${finalNumb}"
    }
}

//Function to format a number in a string. Using asDynamic to use JS, it turns it into local to
// -present the number in US commas.
//Inputs: Number to format, in string form.
fun formatNumber(numberString: String): String {
    val number = numberString.toDoubleOrNull()
    return if (number != null) {
        number.asDynamic().toLocaleString("en-US") as String
    } else {
        "Invalid number"
    }
}

/*
*
* CALCULATION WORK
*
*/

/*     PART 2: SUMMARY     */

/*
    The following functions were used to calculate IRR input.
    After researching online and finding the raw formula, calculating IRR input proved to be hard and pretty advanced.
    Found code online to use. Trusted this code as it looks to be designed well, and generated correct values with testing.
*/
//Function to calculate the NPV
fun npv(cashFlows: List<Long>, rate: Double): Double {
    return cashFlows.withIndex().sumOf { (t, cashFlow) ->
        cashFlow.toDouble() / (1 + rate).pow(t)
    }
}

//Function to calculate the derivative of the NPV
fun npvDerivative(cashFlows: List<Long>, rate: Double): Double {
    return cashFlows.withIndex().sumOf { (t, cashFlow) ->
        -t * cashFlow.toDouble() / (1 + rate).pow(t + 1)
    }
}

//Function to calculate the IRR using the Newton-Raphson method
fun calculateIRR(cashFlows: List<Long>, guess: Double = 0.1, tolerance: Double = 1e-6, maxIterations: Int = 1000): Double {
    var rate = guess
    var iteration = 0
    var difference: Double

    do {
        val npvValue = npv(cashFlows, rate)
        val npvDerivativeValue = npvDerivative(cashFlows, rate)
        val newRate = rate - npvValue / npvDerivativeValue

        difference = abs(newRate - rate)
        rate = newRate
        iteration++
    } while (difference > tolerance && iteration < maxIterations)

    return rate
}

/*     PART 3: CASH FLOWS    */

//Function to calculate a savings list, either for electric or HVAC. It takes the starting Long (amount in cents)
// -and multiplies it by the multiplyBy number to create the list up to 10 columns.
//Inputs: the starting Long number (amount in cents), the multiplyBy double (Excel default is 1.03).
fun savingsListCalculations(start: Long, multiplyBy: Double): MutableList<Long> {
    val savingList = MutableList(10) { 0L }
    //setting first list number to our starting number.
    savingList[0] = start

    //starting at index 1, since we already set index 0 (first element).
    for (i in 1 until savingList.size) {
        savingList[i] = ((savingList[i-1] * multiplyBy).roundToLong()) //TODO rounding issue here.
    }

    return savingList
}

//Function to calculate the Net cash flow row/list. Since rest of project cost is zero besides the first column,
// -it initially sets index[0] to the sum of the rows + project cost, then after only the sum of the rows.
//Inputs: Negative project cost, Energy savings row/list, HVAC savings row/list, and total maintenance savings (constant).
fun netCashFlowCalculations(
    cashflow_projectCost: Long,
    cashflow_energysavinglist: List<Long>,
    cashflow_hvacsavingslist: List<Long>,
    maintenance_savings: Long
): MutableList<Long> {
    val netList = MutableList(10) { 0L }

    //Sum of a column of cells, Q11 =SUM(Q7:Q10)
    netList[0] = (cashflow_projectCost + cashflow_energysavinglist[0] + cashflow_hvacsavingslist[0] + maintenance_savings) //TODO investigate if causing cents issue
    for (i in 1 until cashflow_hvacsavingslist.size) {
        netList[i] = cashflow_energysavinglist[i] + cashflow_hvacsavingslist[i] + maintenance_savings
    }

    return netList
}

//Function to calculate the Cumulative cash flow,
//Inputs: Uses Net Cash flow row.
fun cummulativeCashFlowCalculations(cashflow_netcashflowlist: List<Long>): MutableList<Long> {
    val netList = MutableList(10) { 0L }

    netList[0] = cashflow_netcashflowlist[0]
    for (i in 1 until cashflow_netcashflowlist.size) {
        netList[i] = netList[i-1] + cashflow_netcashflowlist[i]
    }

    return netList
}

//Function to create IRR input row/list to use to display IRR Input & calculate annualized IRR
//Inputs: Negative Project Cost value, Sum of the first cashflow column, and the Net Cash flow row/list.
fun createIRRInput(negProjectCost: Long, cashflowFirstColumnIRRSum: Long, netcashflowlist: List<Long>): MutableList<Long> {
    val netList = netcashflowlist.toMutableList()
    netList.add(1, cashflowFirstColumnIRRSum)
    netList[0] = negProjectCost
    return netList
}

/*     PART 4: CASH FLOWS    */
fun calculateCostOfWaitingList(
    cashflow_energysavinglist: List<Long>,
    cashflow_hvacsavingslist: List<Long>,
    maintenance_savings: Long
): MutableList<Long> {
    val netList = MutableList(5) { 0L }

    netList[0] = cashflow_energysavinglist[0] + cashflow_hvacsavingslist[0] + maintenance_savings
    for (i in 1 until netList.size) {
        netList[i] = netList[i-1] + cashflow_energysavinglist[i] + cashflow_hvacsavingslist[i] + maintenance_savings
    }

    return netList
}